import { Fragment, useEffect, useState } from 'react';
import type { FailedSubmission } from '../types.js';
import { listFailures } from '../api.js';

const REASON_LABELS: Record<string, string> = {
  cors: 'CORS / Origin blocked',
  rate_limit: 'Rate limited',
  payload_too_large: 'Payload too large',
  turnstile: 'Bot check failed',
  validation: 'Validation failed',
  honeypot: 'Honeypot triggered',
  server_error: 'Server error',
  client_not_found: 'Client not found',
};

const REASON_COLORS: Record<string, string> = {
  cors: 'badge-red',
  rate_limit: 'badge-yellow',
  payload_too_large: 'badge-yellow',
  turnstile: 'badge-grey',
  validation: 'badge-yellow',
  honeypot: 'badge-grey',
  server_error: 'badge-red',
  client_not_found: 'badge-red',
};

function parseRawBody(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  // Try JSON first, then urlencoded
  try { return JSON.parse(raw); } catch { /* fall through */ }
  try {
    const params = new URLSearchParams(raw);
    const obj = Object.fromEntries(params.entries());
    return Object.keys(obj).length > 0 ? obj : null;
  } catch { return null; }
}

export default function FailuresList() {
  const [failures, setFailures] = useState<FailedSubmission[]>([]);
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await listFailures({ page, reason: reason || undefined });
      setFailures(res.failures);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [page, reason]);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Failed submissions</h1>
      </div>

      <div className="card">
        <p style={{ color: '#64748b', fontSize: '.85rem', marginBottom: '1rem' }}>
          Every rejected submission is backed up here with its original form data, reason, IP, and time.
          Click <strong>View</strong> on any row to expand the full payload.
        </p>

        <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select value={reason} onChange={(e) => { setReason(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
            <option value="">All reasons</option>
            {Object.entries(REASON_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
              <colgroup>
                <col style={{ width: '150px' }} />
                <col style={{ width: '170px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '130px' }} />
                <col />
                <col style={{ width: '80px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Reason</th>
                  <th>Client</th>
                  <th>IP</th>
                  <th>Detail</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {failures.length === 0 && (
                  <tr><td colSpan={6} style={{ color: '#94a3b8', padding: '1rem 0' }}>No failed submissions.</td></tr>
                )}
                {failures.map((f) => {
                  const parsed = parseRawBody(f.raw_body);
                  const isExpanded = expandedId === f.id;
                  return (
                    <Fragment key={f.id}>
                      <tr>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '.78rem' }}>
                          {new Date(f.created_at * 1000).toLocaleString('en-AU')}
                        </td>
                        <td>
                          <span className={`badge ${REASON_COLORS[f.reason] ?? 'badge-grey'}`}>
                            {REASON_LABELS[f.reason] ?? f.reason}
                          </span>
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.client_id ?? ''}>
                          <code style={{ fontSize: '.78rem' }}>{f.client_id ?? '—'}</code>
                        </td>
                        <td style={{ fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.ip ?? ''}>
                          {f.ip ?? '—'}
                        </td>
                        <td style={{ fontSize: '.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#64748b' }} title={f.error_detail ?? ''}>
                          {f.error_detail ?? '—'}
                        </td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setExpandedId(isExpanded ? null : f.id)}
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f8fafc', padding: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '.5rem .75rem', fontSize: '.85rem' }}>
                              <strong>Failure ID</strong><code>{f.id}</code>
                              <strong>Client</strong><code>{f.client_id ?? '—'}</code>
                              <strong>Origin</strong><code>{f.origin ?? '—'}</code>
                              <strong>IP</strong><span>{f.ip ?? '—'}</span>
                              <strong>User agent</strong><span style={{ wordBreak: 'break-all' }}>{f.user_agent ?? '—'}</span>
                              <strong>Email notified</strong><span>{f.notified_at ? new Date(f.notified_at * 1000).toLocaleString('en-AU') : 'No (throttled or no client)'}</span>
                            </div>

                            <h4 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1rem 0 .5rem' }}>Form data (what the submitter entered)</h4>
                            {parsed ? (
                              <table style={{ minWidth: 'auto', border: '1px solid #e2e8f0' }}>
                                <tbody>
                                  {Object.entries(parsed).map(([k, v]) => (
                                    <tr key={k}>
                                      <th style={{ width: '140px', textAlign: 'left' }}><code>{k}</code></th>
                                      <td style={{ wordBreak: 'break-word' }}>{String(v)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p style={{ color: '#94a3b8', fontSize: '.85rem' }}>
                                Could not parse data (CORS preflight may have rejected before the body was sent, or the body is not JSON / form-urlencoded).
                              </p>
                            )}

                            {f.raw_body && (
                              <>
                                <h4 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1rem 0 .5rem' }}>Raw body</h4>
                                <pre style={{
                                  background: '#0f172a',
                                  color: '#e2e8f0',
                                  padding: '.75rem',
                                  borderRadius: '4px',
                                  fontSize: '.75rem',
                                  overflow: 'auto',
                                  maxHeight: '300px',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}>{f.raw_body}</pre>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
          <span>Page {page}</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={failures.length < 50}>Next →</button>
        </div>
      </div>
    </>
  );
}
